import { Button, Modal, type Column } from "@legal-500/legal500-design-system";
import Papa from "papaparse";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { PaginationControls } from "../../../components/PaginationControls/PaginationControls.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { useQuery } from "../../../hooks/useApi.js";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import { RESEARCH_ID_FIELDS } from "../../../utils/researchIds.js";
import {
  pipelineService,
  type Testimonial,
  type ProcessingItem,
  type ProcessingItemDetail,
  type RawTestimonial,
  type FixesStats,
  type SyncRunStat,
  type CsvImportResult,
} from "../../../api/pipeline.service.js";
import TranslateFrSection from "./TranslateFrSection.js";
import FranceToParisSection from "./FranceToParisSection.js";
import MissingResearchIdsSection from "./MissingResearchIdsSection.js";
import { FranceToParisMappedTab } from "./FranceToParisMapped/index.js";
import { BatchUpdatesTab } from "./BatchUpdates/index.js";
import { MoveBpaTab } from "./MoveBpa/index.js";
import styles from "./PipelineBrowser.module.css";

const PAGE_SIZE = 50;

const TESTIMONIAL_STATUSES = ["APPROVED", "REJECTED", "SUSPENDED"];

function formatDateTime(dateString: string | null): string {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString();
}

function getStatusClass(status: string): string {
  const upper = status.toUpperCase();
  if (upper === "APPROVED" || upper === "PUSHED") return styles.statusApproved;
  if (upper === "REJECTED") return styles.statusRejected;
  if (upper === "NEW") return styles.statusNew;
  if (upper.includes("FAILED")) return styles.statusFailed;
  if (
    upper === "ENRICHED" ||
    upper === "AI_TRANSFORMED" ||
    upper === "HARD_RULES_VALIDATED" ||
    upper === "LANG_DETECTED_AND_NORMALIZED"
  )
    return styles.statusProcessing;
  if (upper === "READY_FOR_REVIEW") return styles.statusPending;
  return styles.statusDefault;
}

function StatusBadge({ status }: { status: string }) {
  return <span className={`${styles.statusBadge} ${getStatusClass(status)}`}>{status}</span>;
}

// --- Testimonials Tab ---

const TAB_IDS = [
  "testimonials",
  "processing-items",
  "raw-testimonials",
  "fixes",
  "batch-updates",
  "move-bpa",
  "france-to-paris",
  "sync-runs",
] as const;

const GATED_TABS: readonly TabId[] = ["batch-updates", "move-bpa"];

type TabId = (typeof TAB_IDS)[number];

function isTabId(value: string | null): value is TabId {
  return (TAB_IDS as readonly (string | null)[]).includes(value);
}

type TestimonialsFilters = {
  id: string;
  externalId: string;
  status: string;
  language: string;
  firmRef: string;
  search: string;
};

const TESTIMONIAL_SORTABLE_COLUMNS = ["status", "language", "firmRef", "lawyerName", "createdAt"];

function TestimonialsTab() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<TestimonialsFilters>({
    id: "",
    externalId: "",
    status: "",
    language: "",
    firmRef: "",
    search: "",
  });
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [selectedItem, setSelectedItem] = useState<Testimonial | null>(null);

  const { data, loading, error, refetch } = useQuery(
    () =>
      pipelineService.getTestimonials({
        page,
        pageSize: PAGE_SIZE,
        id: filters.id || undefined,
        externalId: filters.externalId || undefined,
        status: filters.status || undefined,
        language: filters.language || undefined,
        firmRef: filters.firmRef || undefined,
        search: filters.search || undefined,
        sortField,
        sortDirection,
      }),
    [
      page,
      filters.id,
      filters.externalId,
      filters.status,
      filters.language,
      filters.firmRef,
      filters.search,
      sortField,
      sortDirection,
    ],
  );

  const handleFilterChange = useCallback((key: keyof TestimonialsFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleSort = useCallback(
    (field: string) => {
      if (field === sortField) {
        setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
      setPage(1);
    },
    [sortField],
  );

  const columns: Column<Testimonial>[] = useMemo(
    () => [
      {
        header: "Status",
        accessor: "status" as keyof Testimonial,
        render: (value: unknown) => <StatusBadge status={value as string} />,
      },
      {
        header: "Language",
        accessor: "language" as keyof Testimonial,
      },
      {
        header: "Firm Ref",
        accessor: "firmRef" as keyof Testimonial,
      },
      {
        header: "Lawyer",
        accessor: "lawyerName" as keyof Testimonial,
        render: (value: unknown) => (value as string) || "-",
      },
      {
        header: "Content",
        accessor: "content" as keyof Testimonial,
        render: (value: unknown) => (
          <div className={styles.truncatedContent}>{value as string}</div>
        ),
      },
      {
        header: "Created",
        accessor: "createdAt" as keyof Testimonial,
        render: (value: unknown) => formatDateTime(value as string | null),
      },
    ],
    [],
  );

  if (error) {
    const status = (error as Error & { status?: number }).status;
    if (status === 401 || status === 403) return <UnauthorizedView />;
  }

  const totalPages = data ? Math.ceil(data.pagination.total / PAGE_SIZE) : 0;

  return (
    <>
      <div className={styles.filters}>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>ID</span>
          <input
            className={styles.filterInput}
            placeholder="Filter by ID"
            value={filters.id}
            onChange={e => handleFilterChange("id", e.target.value)}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>External ID</span>
          <input
            className={styles.filterInput}
            placeholder="Filter by External ID"
            value={filters.externalId}
            onChange={e => handleFilterChange("externalId", e.target.value)}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Status</span>
          <select
            className={styles.filterSelect}
            value={filters.status}
            onChange={e => handleFilterChange("status", e.target.value)}
          >
            <option value="">All</option>
            {TESTIMONIAL_STATUSES.map(s => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Language</span>
          <input
            className={styles.filterInput}
            placeholder="e.g. en"
            value={filters.language}
            onChange={e => handleFilterChange("language", e.target.value)}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Firm Ref</span>
          <input
            className={styles.filterInput}
            placeholder="e.g. 12345"
            value={filters.firmRef}
            onChange={e => handleFilterChange("firmRef", e.target.value)}
          />
        </div>
        <div className={`${styles.filterField} ${styles.searchInput}`}>
          <span className={styles.filterLabel}>Search content</span>
          <input
            className={styles.filterInput}
            placeholder="Search in content..."
            value={filters.search}
            onChange={e => handleFilterChange("search", e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <LoadingState message="Loading testimonials..." />
      ) : error ? (
        <EmptyState
          variant="error"
          title="Failed to load testimonials"
          message={error.message}
          action={{ label: "Retry", onClick: () => refetch() }}
        />
      ) : !data?.items.length ? (
        <EmptyState
          variant="empty"
          title="No testimonials found"
          message="Try adjusting filters."
        />
      ) : (
        <>
          <div className={styles.tableContainer}>
            <DataTable
              data={data.items}
              columns={columns}
              onRowClick={setSelectedItem}
              rowKey="id"
              size="small"
              sortableColumnKeys={TESTIMONIAL_SORTABLE_COLUMNS}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          </div>
          <div className={styles.paginationWrapper}>
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              startIndex={(page - 1) * PAGE_SIZE}
              endIndex={page * PAGE_SIZE}
              totalCount={data.pagination.total}
              itemName="testimonials"
              onPageChange={setPage}
            />
          </div>
        </>
      )}

      <Modal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        title="Testimonial Details"
        size="large"
        blurBackground
      >
        {selectedItem && <TestimonialDetail item={selectedItem} />}
      </Modal>
    </>
  );
}

function TestimonialDetail({
  item,
  showCrossRefs = true,
}: {
  item: Testimonial;
  showCrossRefs?: boolean;
}) {
  const [processingItemExists, setProcessingItemExists] = useState<boolean | null>(null);
  const [rawTestimonialExists, setRawTestimonialExists] = useState<boolean | null>(null);
  const [viewProcessingItem, setViewProcessingItem] = useState(false);
  const [viewRawTestimonial, setViewRawTestimonial] = useState(false);
  const [rawTestimonialData, setRawTestimonialData] = useState<RawTestimonial | null>(null);

  useEffect(() => {
    if (!showCrossRefs) return;
    if (item.processingItemId) {
      pipelineService
        .getProcessingItemDetail(item.processingItemId)
        .then(() => setProcessingItemExists(true))
        .catch(() => setProcessingItemExists(false));
    }
    if (item.externalId) {
      pipelineService
        .getRawTestimonials({ id: item.externalId, pageSize: 1 })
        .then(res => {
          setRawTestimonialExists(res.items.length > 0);
          if (res.items.length > 0) setRawTestimonialData(res.items[0]);
        })
        .catch(() => setRawTestimonialExists(false));
    }
  }, [item.processingItemId, item.externalId, showCrossRefs]);

  return (
    <div>
      {showCrossRefs && (
        <div className={styles.crossRefSection}>
          {processingItemExists && (
            <button
              type="button"
              className={styles.crossRefLink}
              onClick={() => setViewProcessingItem(true)}
            >
              View Processing Item
            </button>
          )}
          {rawTestimonialExists && (
            <button
              type="button"
              className={styles.crossRefLink}
              onClick={() => setViewRawTestimonial(true)}
            >
              View Raw Testimonial
            </button>
          )}
        </div>
      )}
      <div className={styles.detailGrid}>
        <span className={styles.detailLabel}>ID</span>
        <span className={styles.detailValue}>{item.id}</span>
        <span className={styles.detailLabel}>External ID</span>
        <span className={styles.detailValue}>{item.externalId || "-"}</span>
        <span className={styles.detailLabel}>Processing Item</span>
        <span className={styles.detailValue}>{item.processingItemId || "-"}</span>
        <span className={styles.detailLabel}>Status</span>
        <span className={styles.detailValue}>
          <StatusBadge status={item.status} />
        </span>
        <span className={styles.detailLabel}>Language</span>
        <span className={styles.detailValue}>{item.language}</span>
        <span className={styles.detailLabel}>Firm Ref</span>
        <span className={styles.detailValue}>{item.firmRef}</span>
        <span className={styles.detailLabel}>Lawyer Name</span>
        <span className={styles.detailValue}>{item.lawyerName || "-"}</span>
        <span className={styles.detailLabel}>Publication ID</span>
        <span className={styles.detailValue}>{item.publicationId ?? "-"}</span>
        <span className={styles.detailLabel}>Practice Area ID</span>
        <span className={styles.detailValue}>{item.practiceAreaId ?? "-"}</span>
        {RESEARCH_ID_FIELDS.map(({ key, label }) => (
          <Fragment key={key}>
            <span className={styles.detailLabel}>{`Research ${label.toLowerCase()}`}</span>
            <span className={styles.detailValue}>{item[key] || "-"}</span>
          </Fragment>
        ))}
        <span className={styles.detailLabel}>Company ID</span>
        <span className={styles.detailValue}>{item.companyId ?? "-"}</span>
        <span className={styles.detailLabel}>Type</span>
        <span className={styles.detailValue}>{item.testimonialType || "-"}</span>
        <span className={styles.detailLabel}>Display Order</span>
        <span className={styles.detailValue}>{item.displayOrder ?? "-"}</span>
        <span className={styles.detailLabel}>Created</span>
        <span className={styles.detailValue}>{formatDateTime(item.createdAt)}</span>
        <span className={styles.detailLabel}>Updated</span>
        <span className={styles.detailValue}>{formatDateTime(item.updatedAt)}</span>
      </div>
      <div className={styles.contentSection}>
        <div className={styles.contentSectionTitle}>Content</div>
        <div className={styles.contentBlock}>{item.content}</div>
      </div>

      <Modal
        isOpen={viewProcessingItem}
        onClose={() => setViewProcessingItem(false)}
        title="Processing Item Details"
        size="large"
        blurBackground
      >
        {viewProcessingItem && item.processingItemId && (
          <ProcessingItemDetailView id={item.processingItemId} showCrossRefs={false} />
        )}
      </Modal>

      <Modal
        isOpen={viewRawTestimonial}
        onClose={() => setViewRawTestimonial(false)}
        title="Raw Testimonial Details"
        size="large"
        blurBackground
      >
        {viewRawTestimonial && rawTestimonialData && (
          <RawTestimonialDetail item={rawTestimonialData} showCrossRefs={false} />
        )}
      </Modal>
    </div>
  );
}

// --- Processing Items Tab ---

type ProcessingItemsFilters = {
  id: string;
  externalId: string;
  status: string;
  langInput: string;
  langOutput: string;
  search: string;
};

const PROCESSING_ITEM_SORTABLE_COLUMNS = [
  "status",
  "langInput",
  "langOutput",
  "retryCount",
  "createdAt",
];

function ProcessingItemsTab() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<ProcessingItemsFilters>({
    id: "",
    externalId: "",
    status: "",
    langInput: "",
    langOutput: "",
    search: "",
  });
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery(
    () =>
      pipelineService.getProcessingItems({
        page,
        pageSize: PAGE_SIZE,
        id: filters.id || undefined,
        externalId: filters.externalId || undefined,
        status: filters.status || undefined,
        langInput: filters.langInput || undefined,
        langOutput: filters.langOutput || undefined,
        search: filters.search || undefined,
        sortField,
        sortDirection,
      }),
    [
      page,
      filters.id,
      filters.externalId,
      filters.status,
      filters.langInput,
      filters.langOutput,
      filters.search,
      sortField,
      sortDirection,
    ],
  );

  const handleFilterChange = useCallback((key: keyof ProcessingItemsFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleSort = useCallback(
    (field: string) => {
      if (field === sortField) {
        setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
      setPage(1);
    },
    [sortField],
  );

  const columns: Column<ProcessingItem>[] = useMemo(
    () => [
      {
        header: "Status",
        accessor: "status" as keyof ProcessingItem,
        render: (value: unknown) => <StatusBadge status={(value as string) || "UNKNOWN"} />,
      },
      {
        header: "Lang In",
        accessor: "langInput" as keyof ProcessingItem,
        render: (value: unknown) => (value as string) || "-",
      },
      {
        header: "Lang Out",
        accessor: "langOutput" as keyof ProcessingItem,
        render: (value: unknown) => (value as string) || "-",
      },
      {
        header: "Transformations",
        accessor: "transformations" as keyof ProcessingItem,
        render: (value: unknown) => (value as string) || "-",
      },
      {
        header: "Retries",
        accessor: "retryCount" as keyof ProcessingItem,
      },
      {
        header: "Last Error",
        accessor: "lastError" as keyof ProcessingItem,
        render: (value: unknown) => {
          const err = value as string | null;
          if (!err) return "-";
          return <div className={styles.truncatedContent}>{err}</div>;
        },
      },
      {
        header: "Created",
        accessor: "createdAt" as keyof ProcessingItem,
        render: (value: unknown) => formatDateTime(value as string | null),
      },
    ],
    [],
  );

  if (error) {
    const status = (error as Error & { status?: number }).status;
    if (status === 401 || status === 403) return <UnauthorizedView />;
  }

  const totalPages = data ? Math.ceil(data.pagination.total / PAGE_SIZE) : 0;
  const statuses = data?.statuses ?? [];

  return (
    <>
      <div className={styles.filters}>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>ID</span>
          <input
            className={styles.filterInput}
            placeholder="Filter by ID"
            value={filters.id}
            onChange={e => handleFilterChange("id", e.target.value)}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>External ID</span>
          <input
            className={styles.filterInput}
            placeholder="Filter by External ID"
            value={filters.externalId}
            onChange={e => handleFilterChange("externalId", e.target.value)}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Status</span>
          <select
            className={styles.filterSelect}
            value={filters.status}
            onChange={e => handleFilterChange("status", e.target.value)}
          >
            <option value="">All</option>
            {statuses.map(s => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Lang Input</span>
          <input
            className={styles.filterInput}
            placeholder="e.g. en"
            value={filters.langInput}
            onChange={e => handleFilterChange("langInput", e.target.value)}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Lang Output</span>
          <input
            className={styles.filterInput}
            placeholder="e.g. fr"
            value={filters.langOutput}
            onChange={e => handleFilterChange("langOutput", e.target.value)}
          />
        </div>
        <div className={`${styles.filterField} ${styles.searchInput}`}>
          <span className={styles.filterLabel}>Search content</span>
          <input
            className={styles.filterInput}
            placeholder="Search in content input..."
            value={filters.search}
            onChange={e => handleFilterChange("search", e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <LoadingState message="Loading processing items..." />
      ) : error ? (
        <EmptyState
          variant="error"
          title="Failed to load processing items"
          message={error.message}
          action={{ label: "Retry", onClick: () => refetch() }}
        />
      ) : !data?.items.length ? (
        <EmptyState
          variant="empty"
          title="No processing items found"
          message="Try adjusting filters."
        />
      ) : (
        <>
          <div className={styles.tableContainer}>
            <DataTable
              data={data.items}
              columns={columns}
              onRowClick={row => setSelectedId(row.id)}
              rowKey="id"
              size="small"
              sortableColumnKeys={PROCESSING_ITEM_SORTABLE_COLUMNS}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          </div>
          <div className={styles.paginationWrapper}>
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              startIndex={(page - 1) * PAGE_SIZE}
              endIndex={page * PAGE_SIZE}
              totalCount={data.pagination.total}
              itemName="processing items"
              onPageChange={setPage}
            />
          </div>
        </>
      )}

      <Modal
        isOpen={!!selectedId}
        onClose={() => setSelectedId(null)}
        title="Processing Item Details"
        size="large"
        blurBackground
      >
        {selectedId && <ProcessingItemDetailView id={selectedId} />}
      </Modal>
    </>
  );
}

function ProcessingItemDetailView({
  id,
  showCrossRefs = true,
}: {
  id: string;
  showCrossRefs?: boolean;
}) {
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [rawTestimonialExists, setRawTestimonialExists] = useState<boolean | null>(null);
  const [testimonialsExist, setTestimonialsExist] = useState<boolean | null>(null);
  const [viewRawTestimonial, setViewRawTestimonial] = useState(false);
  const [viewTestimonials, setViewTestimonials] = useState(false);
  const [rawTestimonialData, setRawTestimonialData] = useState<RawTestimonial | null>(null);
  const [testimonialsList, setTestimonialsList] = useState<Testimonial[]>([]);
  const [selectedTestimonial, setSelectedTestimonial] = useState<Testimonial | null>(null);
  const { data, loading, error } = useQuery(
    () => pipelineService.getProcessingItemDetail(id),
    [id],
  );

  useEffect(() => {
    if (!showCrossRefs || !data?.externalId) return;
    pipelineService
      .getRawTestimonials({ id: data.externalId, pageSize: 1 })
      .then(res => {
        setRawTestimonialExists(res.items.length > 0);
        if (res.items.length > 0) setRawTestimonialData(res.items[0]);
      })
      .catch(() => setRawTestimonialExists(false));
    pipelineService
      .getTestimonials({ externalId: data.externalId, pageSize: 1 })
      .then(res => setTestimonialsExist(res.pagination.total > 0))
      .catch(() => setTestimonialsExist(false));
  }, [data?.externalId, showCrossRefs]);

  const handleViewTestimonials = useCallback(async () => {
    if (!data?.externalId) return;
    const res = await pipelineService.getTestimonials({
      externalId: data.externalId,
      pageSize: 100,
    });
    setTestimonialsList(res.items);
    setViewTestimonials(true);
  }, [data?.externalId]);

  if (loading) return <LoadingState message="Loading details..." />;
  if (error) return <EmptyState variant="error" title="Failed to load" message={error.message} />;
  if (!data) return null;

  return (
    <div>
      {showCrossRefs && (
        <div className={styles.crossRefSection}>
          {rawTestimonialExists && (
            <button
              type="button"
              className={styles.crossRefLink}
              onClick={() => setViewRawTestimonial(true)}
            >
              View Raw Testimonial
            </button>
          )}
          {testimonialsExist && (
            <button type="button" className={styles.crossRefLink} onClick={handleViewTestimonials}>
              View Testimonials
            </button>
          )}
        </div>
      )}
      <div className={styles.detailGrid}>
        <span className={styles.detailLabel}>ID</span>
        <span className={styles.detailValue}>{data.id}</span>
        <span className={styles.detailLabel}>External ID</span>
        <span className={styles.detailValue}>{data.externalId || "-"}</span>
        <span className={styles.detailLabel}>Parent ID</span>
        <span className={styles.detailValue}>{data.parentProcessingItemId || "-"}</span>
        <span className={styles.detailLabel}>Status</span>
        <span className={styles.detailValue}>
          <StatusBadge status={data.status || "UNKNOWN"} />
        </span>
        <span className={styles.detailLabel}>Lang Input</span>
        <span className={styles.detailValue}>{data.langInput || "-"}</span>
        <span className={styles.detailLabel}>Lang Output</span>
        <span className={styles.detailValue}>{data.langOutput || "-"}</span>
        <span className={styles.detailLabel}>Transformations</span>
        <span className={styles.detailValue}>{data.transformations || "-"}</span>
        <span className={styles.detailLabel}>Retry Count</span>
        <span className={styles.detailValue}>{data.retryCount}</span>
        <span className={styles.detailLabel}>Created</span>
        <span className={styles.detailValue}>{formatDateTime(data.createdAt)}</span>
        <span className={styles.detailLabel}>Updated</span>
        <span className={styles.detailValue}>{formatDateTime(data.updatedAt)}</span>
      </div>

      {data.lastError && (
        <div className={styles.contentSection}>
          <div className={styles.contentSectionTitle}>Last Error</div>
          <div className={`${styles.contentBlock} ${styles.errorBlock}`}>
            {data.lastError}
            {data.lastErrorAt ? `\n\nAt: ${formatDateTime(data.lastErrorAt)}` : ""}
          </div>
        </div>
      )}

      {data.contentInput && (
        <div className={styles.contentSection}>
          <div className={styles.contentSectionTitle}>Content Input</div>
          <div className={styles.contentBlock}>{data.contentInput}</div>
        </div>
      )}

      {data.contentTransformed && (
        <div className={styles.contentSection}>
          <div className={styles.contentSectionTitle}>Content Transformed</div>
          <div className={styles.contentBlock}>{data.contentTransformed}</div>
        </div>
      )}

      {data.contentOutput && (
        <div className={styles.contentSection}>
          <div className={styles.contentSectionTitle}>Content Output</div>
          <div className={styles.contentBlock}>{data.contentOutput}</div>
        </div>
      )}

      {data.metadata != null && (
        <div className={styles.contentSection}>
          <div className={styles.contentSectionTitle}>Metadata</div>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => setMetadataOpen(true)}
          >
            Show Metadata
          </Button>
          <Modal
            isOpen={metadataOpen}
            onClose={() => setMetadataOpen(false)}
            title="Processing Item Metadata"
            size="large"
            blurBackground
          >
            <div className={`${styles.contentBlock} ${styles.metadataBlock}`}>
              {JSON.stringify(data.metadata, null, 2)}
            </div>
          </Modal>
        </div>
      )}

      <Modal
        isOpen={viewRawTestimonial}
        onClose={() => setViewRawTestimonial(false)}
        title="Raw Testimonial Details"
        size="large"
        blurBackground
      >
        {viewRawTestimonial && rawTestimonialData && (
          <RawTestimonialDetail item={rawTestimonialData} showCrossRefs={false} />
        )}
      </Modal>

      <Modal
        isOpen={viewTestimonials}
        onClose={() => {
          setViewTestimonials(false);
          setSelectedTestimonial(null);
        }}
        title="Testimonials"
        size="large"
        blurBackground
      >
        {viewTestimonials && (
          <>
            <CrossRefTestimonialsList items={testimonialsList} onSelect={setSelectedTestimonial} />
            <Modal
              isOpen={!!selectedTestimonial}
              onClose={() => setSelectedTestimonial(null)}
              title="Testimonial Details"
              size="large"
              blurBackground
            >
              {selectedTestimonial && (
                <TestimonialDetail item={selectedTestimonial} showCrossRefs={false} />
              )}
            </Modal>
          </>
        )}
      </Modal>
    </div>
  );
}

// --- Raw Testimonials Tab ---

type RawTestimonialsFilters = {
  id: string;
  publication: string;
  edition: string;
  firmRef: string;
  search: string;
};

const RAW_TESTIMONIAL_SORTABLE_COLUMNS = [
  "publication",
  "edition",
  "firmRef",
  "lawyerName",
  "updatedAt",
];

function RawTestimonialsTab() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<RawTestimonialsFilters>({
    id: "",
    publication: "",
    edition: "",
    firmRef: "",
    search: "",
  });
  const [sortField, setSortField] = useState<string>("updatedAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [selectedItem, setSelectedItem] = useState<RawTestimonial | null>(null);

  const { data, loading, error, refetch } = useQuery(
    () =>
      pipelineService.getRawTestimonials({
        page,
        pageSize: PAGE_SIZE,
        id: filters.id || undefined,
        publication: filters.publication || undefined,
        edition: filters.edition || undefined,
        firmRef: filters.firmRef || undefined,
        search: filters.search || undefined,
        sortField,
        sortDirection,
      }),
    [
      page,
      filters.id,
      filters.publication,
      filters.edition,
      filters.firmRef,
      filters.search,
      sortField,
      sortDirection,
    ],
  );

  const handleFilterChange = useCallback((key: keyof RawTestimonialsFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleSort = useCallback(
    (field: string) => {
      if (field === sortField) {
        setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
      setPage(1);
    },
    [sortField],
  );

  const columns: Column<RawTestimonial>[] = useMemo(
    () => [
      {
        header: "ID",
        accessor: "id" as keyof RawTestimonial,
        render: (value: unknown) => {
          const id = value as string;
          return id.length > 20 ? `${id.slice(0, 20)}...` : id;
        },
      },
      {
        header: "Publication",
        accessor: "publication" as keyof RawTestimonial,
      },
      {
        header: "Edition",
        accessor: "edition" as keyof RawTestimonial,
        render: (value: unknown) => (value as string) || "-",
      },
      {
        header: "Firm Ref",
        accessor: "firmRef" as keyof RawTestimonial,
        render: (value: unknown) => (value as number | null) ?? "-",
      },
      {
        header: "Firm Name",
        accessor: "firmName" as keyof RawTestimonial,
        render: (value: unknown) => (value as string) || "-",
      },
      {
        header: "Lawyer",
        accessor: "lawyerName" as keyof RawTestimonial,
        render: (value: unknown) => (value as string) || "-",
      },
      {
        header: "Content",
        accessor: "content" as keyof RawTestimonial,
        render: (value: unknown) => (
          <div className={styles.truncatedContent}>{(value as string) || "-"}</div>
        ),
      },
      {
        header: "Updated",
        accessor: "updatedAt" as keyof RawTestimonial,
        render: (value: unknown) => formatDateTime(value as string | null),
      },
    ],
    [],
  );

  if (error) {
    const status = (error as Error & { status?: number }).status;
    if (status === 401 || status === 403) return <UnauthorizedView />;
  }

  const totalPages = data ? Math.ceil(data.pagination.total / PAGE_SIZE) : 0;

  return (
    <>
      <div className={styles.filters}>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>ID</span>
          <input
            className={styles.filterInput}
            placeholder="Filter by ID"
            value={filters.id}
            onChange={e => handleFilterChange("id", e.target.value)}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Publication</span>
          <input
            className={styles.filterInput}
            placeholder="e.g. legal500"
            value={filters.publication}
            onChange={e => handleFilterChange("publication", e.target.value)}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Edition</span>
          <input
            className={styles.filterInput}
            placeholder="e.g. 2026"
            value={filters.edition}
            onChange={e => handleFilterChange("edition", e.target.value)}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Firm Ref</span>
          <input
            className={styles.filterInput}
            placeholder="e.g. 12345"
            value={filters.firmRef}
            onChange={e => handleFilterChange("firmRef", e.target.value)}
          />
        </div>
        <div className={`${styles.filterField} ${styles.searchInput}`}>
          <span className={styles.filterLabel}>Search content</span>
          <input
            className={styles.filterInput}
            placeholder="Search in content..."
            value={filters.search}
            onChange={e => handleFilterChange("search", e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <LoadingState message="Loading raw testimonials..." />
      ) : error ? (
        <EmptyState
          variant="error"
          title="Failed to load raw testimonials"
          message={error.message}
          action={{ label: "Retry", onClick: () => refetch() }}
        />
      ) : !data?.items.length ? (
        <EmptyState
          variant="empty"
          title="No raw testimonials found"
          message="Try adjusting filters."
        />
      ) : (
        <>
          <div className={styles.tableContainer}>
            <DataTable
              data={data.items}
              columns={columns}
              onRowClick={setSelectedItem}
              rowKey="id"
              size="small"
              sortableColumnKeys={RAW_TESTIMONIAL_SORTABLE_COLUMNS}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          </div>
          <div className={styles.paginationWrapper}>
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              startIndex={(page - 1) * PAGE_SIZE}
              endIndex={page * PAGE_SIZE}
              totalCount={data.pagination.total}
              itemName="raw testimonials"
              onPageChange={setPage}
            />
          </div>
        </>
      )}

      <Modal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        title="Raw Testimonial Details"
        size="large"
        blurBackground
      >
        {selectedItem && <RawTestimonialDetail item={selectedItem} />}
      </Modal>
    </>
  );
}

function RawTestimonialDetail({
  item,
  showCrossRefs = true,
}: {
  item: RawTestimonial;
  showCrossRefs?: boolean;
}) {
  const [processingItemsExist, setProcessingItemsExist] = useState<boolean | null>(null);
  const [testimonialsExist, setTestimonialsExist] = useState<boolean | null>(null);
  const [viewProcessingItemId, setViewProcessingItemId] = useState<string | null>(null);
  const [viewTestimonials, setViewTestimonials] = useState(false);
  const [testimonialsList, setTestimonialsList] = useState<Testimonial[]>([]);
  const [selectedTestimonial, setSelectedTestimonial] = useState<Testimonial | null>(null);

  useEffect(() => {
    if (!showCrossRefs) return;
    pipelineService
      .getProcessingItems({ externalId: item.id, pageSize: 1 })
      .then(res => setProcessingItemsExist(res.pagination.total > 0))
      .catch(() => setProcessingItemsExist(false));
    pipelineService
      .getTestimonials({ externalId: item.id, pageSize: 1 })
      .then(res => setTestimonialsExist(res.pagination.total > 0))
      .catch(() => setTestimonialsExist(false));
  }, [item.id, showCrossRefs]);

  const handleViewProcessingItem = useCallback(async () => {
    const res = await pipelineService.getProcessingItems({ externalId: item.id, pageSize: 1 });
    if (res.items.length > 0) {
      setViewProcessingItemId(res.items[0].id);
    }
  }, [item.id]);

  const handleViewTestimonials = useCallback(async () => {
    const res = await pipelineService.getTestimonials({ externalId: item.id, pageSize: 100 });
    setTestimonialsList(res.items);
    setViewTestimonials(true);
  }, [item.id]);

  return (
    <div>
      {showCrossRefs && (
        <div className={styles.crossRefSection}>
          {processingItemsExist && (
            <button
              type="button"
              className={styles.crossRefLink}
              onClick={handleViewProcessingItem}
            >
              View Processing Item
            </button>
          )}
          {testimonialsExist && (
            <button type="button" className={styles.crossRefLink} onClick={handleViewTestimonials}>
              View Testimonials
            </button>
          )}
        </div>
      )}
      <div className={styles.detailGrid}>
        <span className={styles.detailLabel}>ID</span>
        <span className={styles.detailValue}>{item.id}</span>
        <span className={styles.detailLabel}>Code</span>
        <span className={styles.detailValue}>{item.code || "-"}</span>
        <span className={styles.detailLabel}>Editorial Category ID</span>
        <span className={styles.detailValue}>{item.editorialCategoryId ?? "-"}</span>
        <span className={styles.detailLabel}>Content Type</span>
        <span className={styles.detailValue}>{item.contentType || "-"}</span>
        <span className={styles.detailLabel}>Publication</span>
        <span className={styles.detailValue}>{item.publication}</span>
        <span className={styles.detailLabel}>Edition</span>
        <span className={styles.detailValue}>{item.edition || "-"}</span>
        <span className={styles.detailLabel}>Publication ID</span>
        <span className={styles.detailValue}>{item.publicationId}</span>
        <span className={styles.detailLabel}>Firm Name</span>
        <span className={styles.detailValue}>{item.firmName || "-"}</span>
        <span className={styles.detailLabel}>Firm Ref</span>
        <span className={styles.detailValue}>{item.firmRef ?? "-"}</span>
        <span className={styles.detailLabel}>Company ID</span>
        <span className={styles.detailValue}>{item.companyId ?? "-"}</span>
        <span className={styles.detailLabel}>Lawyer Name</span>
        <span className={styles.detailValue}>{item.lawyerName || "-"}</span>
        <span className={styles.detailLabel}>Contact ID</span>
        <span className={styles.detailValue}>{item.contactId || "-"}</span>
        <span className={styles.detailLabel}>Editorial Post ID</span>
        <span className={styles.detailValue}>{item.editorialPostId ?? "-"}</span>
        <span className={styles.detailLabel}>Table ID</span>
        <span className={styles.detailValue}>{item.tableId ?? "-"}</span>
        <span className={styles.detailLabel}>Updated</span>
        <span className={styles.detailValue}>{formatDateTime(item.updatedAt)}</span>
      </div>
      <div className={styles.contentSection}>
        <div className={styles.contentSectionTitle}>Content</div>
        <div className={styles.contentBlock}>{item.content || "-"}</div>
      </div>

      <Modal
        isOpen={!!viewProcessingItemId}
        onClose={() => setViewProcessingItemId(null)}
        title="Processing Item Details"
        size="large"
        blurBackground
      >
        {viewProcessingItemId && (
          <ProcessingItemDetailView id={viewProcessingItemId} showCrossRefs={false} />
        )}
      </Modal>

      <Modal
        isOpen={viewTestimonials}
        onClose={() => {
          setViewTestimonials(false);
          setSelectedTestimonial(null);
        }}
        title="Testimonials"
        size="large"
        blurBackground
      >
        {viewTestimonials && (
          <>
            <CrossRefTestimonialsList items={testimonialsList} onSelect={setSelectedTestimonial} />
            <Modal
              isOpen={!!selectedTestimonial}
              onClose={() => setSelectedTestimonial(null)}
              title="Testimonial Details"
              size="large"
              blurBackground
            >
              {selectedTestimonial && (
                <TestimonialDetail item={selectedTestimonial} showCrossRefs={false} />
              )}
            </Modal>
          </>
        )}
      </Modal>
    </div>
  );
}

// --- Cross-ref list helpers ---

function CrossRefTestimonialsList({
  items,
  onSelect,
}: {
  items: Testimonial[];
  onSelect: (item: Testimonial) => void;
}) {
  if (!items.length) return <EmptyState variant="empty" title="No testimonials found" message="" />;
  return (
    <div className={styles.tableContainer}>
      <table className={styles.crossRefTable}>
        <thead>
          <tr>
            <th>Status</th>
            <th>Language</th>
            <th>Firm Ref</th>
            <th>Lawyer</th>
            <th>Content</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id} onClick={() => onSelect(item)} className={styles.crossRefRow}>
              <td>
                <StatusBadge status={item.status} />
              </td>
              <td>{item.language}</td>
              <td>{item.firmRef}</td>
              <td>{item.lawyerName || "-"}</td>
              <td>
                <div className={styles.truncatedContent}>{item.content}</div>
              </td>
              <td>{formatDateTime(item.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- Fixes Tab ---

function FixesTab() {
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const { data, loading, error, refetch } = useQuery(() => pipelineService.getFixesStats(), []);

  return (
    <>
      <div className={styles.contentSection}>
        <div className={styles.contentSectionTitle}>Missing company_id</div>
        {loading ? (
          <LoadingState message="Loading stats..." />
        ) : error ? (
          <EmptyState
            variant="error"
            title="Failed to load stats"
            message={error.message}
            action={{ label: "Retry", onClick: () => refetch() }}
          />
        ) : (
          <div className={styles.syncStateRow}>
            <span>Testimonials without company_id: </span>
            <strong>{data?.missingCompanyId ?? "-"}</strong>
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={updating}
              onClick={async () => {
                const confirmed = window.confirm(
                  "This will update company_id in testimonials and companyId in processing_items metadata using data from api_l500_client_survey_testimonials.\n\nContinue?",
                );
                if (!confirmed) return;
                setUpdating(true);
                setMessage(null);
                try {
                  const result = await pipelineService.updateMissingCompanyId();
                  setMessage(result.message);
                  refetch();
                } catch (err) {
                  setMessage(err instanceof Error ? err.message : "Update failed");
                } finally {
                  setUpdating(false);
                }
              }}
            >
              {updating ? "Updating..." : "Update companyId"}
            </Button>
          </div>
        )}
        {message && <div className={styles.syncStateMessage}>{message}</div>}
      </div>

      <RequeueSection />

      <TranslateFrSection />

      <FranceToParisSection />

      <MissingResearchIdsSection />
    </>
  );
}

// --- Requeue Section ---

const REQUEUEABLE_STATUSES = [
  "NEW",
  "HARD_RULES_VALIDATED",
  "ENRICHED",
  "AI_TRANSFORMED",
  "LANG_DETECTED_AND_NORMALIZED",
  "HARD_CHECK_FAILED",
  "ENRICHMENT_FAILED",
  "AI_TRANSFORM_FAILED",
  "TRANSLATION_FAILED",
  "SEND_TO_APPROVAL_FAILED",
];

function RequeueSection() {
  const [status, setStatus] = useState(REQUEUEABLE_STATUSES[0]);
  const [limitInput, setLimitInput] = useState("");
  const [requeuing, setRequeuing] = useState(false);
  const [resultMessage, setResultMessage] = useState<string | null>(null);

  return (
    <div className={styles.contentSection}>
      <div className={styles.contentSectionTitle}>Requeue processing items</div>
      <div className={styles.syncStateRow}>
        <select
          className={styles.filterSelect}
          value={status}
          onChange={e => setStatus(e.target.value)}
        >
          {REQUEUEABLE_STATUSES.map(s => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          className={styles.filterInput}
          type="number"
          min={1}
          placeholder="Limit (empty = all)"
          value={limitInput}
          onChange={e => setLimitInput(e.target.value)}
          style={{ width: 160 }}
        />
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={requeuing}
          onClick={async () => {
            const limit = limitInput.trim() ? Number(limitInput.trim()) : undefined;
            const desc = limit ? `${limit} items` : "all items";
            const confirmed = window.confirm(
              `Requeue ${desc} with status ${status}?\n\nThis will move them back to the processing queue.`,
            );
            if (!confirmed) return;
            setRequeuing(true);
            setResultMessage(null);
            try {
              const result = await pipelineService.requeueProcessingItems(status, limit);
              setResultMessage(`Requeued ${result.requeued} items to ${result.targetQueue}`);
            } catch (err) {
              setResultMessage(`Error: ${err instanceof Error ? err.message : "Requeue failed"}`);
            } finally {
              setRequeuing(false);
            }
          }}
        >
          {requeuing ? "Requeuing..." : "Requeue"}
        </Button>
      </div>
      {resultMessage && <div className={styles.syncStateMessage}>{resultMessage}</div>}
    </div>
  );
}

// --- Sync Runs Tab ---

function parseNullableInt(value: string | null | undefined): number | null {
  if (value === "" || value == null) return null;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? null : parsed;
}

function mapRowToRawTestimonial(row: Record<string, string>): RawTestimonial | null {
  if (!row.id) return null;
  if (!row.updated_at) return null;
  const publicationId = parseInt(row.publication_id, 10);
  if (isNaN(publicationId)) return null;
  return {
    id: row.id,
    code: row.code || null,
    editorialCategoryId: parseNullableInt(row.editorial_category_id),
    contentType: row.content_type || null,
    content: row.content || null,
    lawyerName: row.lawyer_name || null,
    publication: row.publication || null,
    edition: row.edition || null,
    publicationId,
    firmName: row.firm_name || null,
    firmRef: parseNullableInt(row.firm_ref),
    companyId: parseNullableInt(row.company_id),
    editorialPostId: parseNullableInt(row.editorial_post_id),
    contactId: row.contact_id || null,
    tableId: parseNullableInt(row.table_id),
    updatedAt: row.updated_at,
  };
}

const BATCH_SIZE_OPTIONS = [50, 100, 250, 500] as const;

function CsvImportPanel() {
  const [batchSize, setBatchSize] = useState<number>(500);
  const [status, setStatus] = useState<"idle" | "parsing" | "uploading">("idle");
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [result, setResult] = useState<{
    inserted: number;
    alreadyExisted: number;
    invalid: number;
    total: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentBatchSize = batchSize;
      const file = e.target.files?.[0];
      if (!file) return;

      setStatus("parsing");
      setProgress(null);
      setResult(null);
      setError(null);

      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: async results => {
          const rows = results.data;
          const records: RawTestimonial[] = [];
          let invalid = 0;

          for (const row of rows) {
            const record = mapRowToRawTestimonial(row);
            if (record) {
              records.push(record);
            } else {
              invalid++;
            }
          }

          const totalBatches = Math.ceil(records.length / currentBatchSize);
          let totalInserted = 0;
          let totalSent = 0;

          setStatus("uploading");

          try {
            for (let i = 0; i < totalBatches; i++) {
              setProgress({ current: i + 1, total: totalBatches });
              const batch = records.slice(i * currentBatchSize, (i + 1) * currentBatchSize);
              const batchResult: CsvImportResult =
                await pipelineService.importRawTestimonials(batch);
              totalInserted += batchResult.inserted;
              totalSent += batchResult.total;
            }
            setResult({
              inserted: totalInserted,
              alreadyExisted: totalSent - totalInserted,
              invalid,
              total: rows.length,
            });
          } catch (err) {
            setError(err instanceof Error ? err.message : "Import failed");
          } finally {
            setStatus("idle");
            e.target.value = "";
          }
        },
        error: err => {
          setError(err.message);
          setStatus("idle");
          e.target.value = "";
        },
      });
    },
    [batchSize],
  );

  return (
    <div className={styles.syncStatePanel}>
      <div className={styles.contentSectionTitle}>Import Raw Testimonials from CSV</div>
      <div className={styles.syncStateRow}>
        <span className={styles.syncStateLabel}>Batch size:</span>
        <select
          value={batchSize}
          disabled={status !== "idle"}
          onChange={e => setBatchSize(Number(e.target.value))}
        >
          {BATCH_SIZE_OPTIONS.map(size => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.syncStateRow}>
        <span className={styles.syncStateLabel}>CSV file:</span>
        <input type="file" accept=".csv" disabled={status !== "idle"} onChange={handleFileChange} />
      </div>
      {status === "parsing" && <div className={styles.syncStateMessage}>Parsing CSV...</div>}
      {status === "uploading" && progress && (
        <div className={styles.syncStateMessage}>
          Uploading batch {progress.current} / {progress.total}...
        </div>
      )}
      {result && (
        <div className={styles.syncStateMessage}>
          Done: {result.inserted} inserted, {result.alreadyExisted} already existed,{" "}
          {result.invalid} invalid — {result.total} total rows
        </div>
      )}
      {error && <div className={styles.syncStateMessage}>Error: {error}</div>}
    </div>
  );
}

function formatDuration(startedAt: string, completedAt: string): string {
  const diffMs = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  const totalSeconds = Math.round(diffMs / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

function SyncRunsTab() {
  const { data, loading, error, refetch } = useQuery(() => pipelineService.getSyncRunStats(), []);

  const columns: Column<SyncRunStat>[] = useMemo(
    () => [
      {
        header: "Started At",
        accessor: "startedAt" as keyof SyncRunStat,
        render: (value: unknown) => formatDateTime(value as string),
      },
      {
        header: "Completed At",
        accessor: "completedAt" as keyof SyncRunStat,
        render: (value: unknown) => formatDateTime(value as string),
      },
      {
        header: "Duration",
        accessor: "startedAt" as keyof SyncRunStat,
        render: (_value: unknown, row: SyncRunStat) =>
          formatDuration(row.startedAt, row.completedAt),
      },
      {
        header: "Processed",
        accessor: "processed" as keyof SyncRunStat,
      },
      {
        header: "Created",
        accessor: "created" as keyof SyncRunStat,
      },
      {
        header: "Skipped",
        accessor: "skipped" as keyof SyncRunStat,
      },
      {
        header: "Batches",
        accessor: "batches" as keyof SyncRunStat,
      },
    ],
    [],
  );

  const status = error ? (error as Error & { status?: number }).status : undefined;
  if (status === 401 || status === 403) return <UnauthorizedView />;

  return (
    <>
      <CsvImportPanel />
      <SyncStatePanel />
      {loading ? (
        <LoadingState message="Loading sync runs..." />
      ) : error ? (
        <EmptyState
          variant="error"
          title="Failed to load sync runs"
          message={error.message}
          action={{ label: "Retry", onClick: () => refetch() }}
        />
      ) : data?.runs.length ? (
        <div className={styles.tableContainer}>
          <DataTable data={data.runs} columns={columns} rowKey="id" size="small" />
        </div>
      ) : (
        <EmptyState variant="empty" title="No sync runs found" message="No runs recorded yet." />
      )}
    </>
  );
}

// --- Sync State Panel ---

function SyncStatePanel() {
  const [editValue, setEditValue] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery(() => pipelineService.getSyncState(), []);

  const handleEdit = useCallback(() => {
    setEditValue(data?.syncState?.lastDataDt ?? "");
    setEditing(true);
    setMessage(null);
  }, [data]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setMessage(null);
    try {
      const result = await pipelineService.updateSyncState(editValue || null);
      setMessage(result.message);
      setEditing(false);
      refetch();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setSaving(false);
    }
  }, [editValue, refetch]);

  if (loading) return null;

  const syncState = data?.syncState ?? null;

  return (
    <div className={styles.syncStatePanel}>
      <div className={styles.syncStateRow}>
        <span className={styles.syncStateLabel}>Last Sync:</span>
        <span>{syncState ? formatDateTime(syncState.lastSyncAt) : "-"}</span>
        <span className={styles.syncStateLabel}>Last Data Dt:</span>
        {editing ? (
          <>
            <input
              className={styles.filterInput}
              type="text"
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              placeholder="ISO date, e.g. 2026-01-01T00:00:00.000Z"
              style={{ minWidth: 280 }}
            />
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <span>
              {syncState?.lastDataDt ? formatDateTime(syncState.lastDataDt) : "null (epoch)"}
            </span>
            <Button variant="outlined" color="primary" size="small" onClick={handleEdit}>
              Edit
            </Button>
          </>
        )}
      </div>
      {message && <div className={styles.syncStateMessage}>{message}</div>}
    </div>
  );
}

// --- Main Page ---

function PipelineBrowserPage() {
  const [searchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState<TabId>(() => {
    const requested = searchParams.get("tab");
    return isTabId(requested) ? requested : "testimonials";
  });
  const { hasFlag, isLoading: flagsLoading } = useFeatureFlags();
  const { hasPermission } = usePermissions();
  const canSeeBatchUpdates =
    !flagsLoading && hasFlag("pipeline-batch-updates") && hasPermission("developer.job_runs");
  const activeTab =
    GATED_TABS.includes(selectedTab) && !canSeeBatchUpdates ? "testimonials" : selectedTab;

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Pipeline Browser"
        subtitle="Browse testimonials, processing items, and raw testimonials from the content processor pipeline"
      />

      <div className={styles.content}>
        <div className={styles.tabs}>
          <button
            type="button"
            className={activeTab === "testimonials" ? styles.tabActive : styles.tab}
            onClick={() => setSelectedTab("testimonials")}
          >
            Testimonials
          </button>
          <button
            type="button"
            className={activeTab === "processing-items" ? styles.tabActive : styles.tab}
            onClick={() => setSelectedTab("processing-items")}
          >
            Processing Items
          </button>
          <button
            type="button"
            className={activeTab === "raw-testimonials" ? styles.tabActive : styles.tab}
            onClick={() => setSelectedTab("raw-testimonials")}
          >
            Raw Testimonials
          </button>
          <button
            type="button"
            className={activeTab === "fixes" ? styles.tabActive : styles.tab}
            onClick={() => setSelectedTab("fixes")}
          >
            Fixes
          </button>
          {canSeeBatchUpdates && (
            <button
              type="button"
              className={activeTab === "batch-updates" ? styles.tabActive : styles.tab}
              onClick={() => setSelectedTab("batch-updates")}
            >
              Batch Updates
            </button>
          )}
          {canSeeBatchUpdates && (
            <button
              type="button"
              className={activeTab === "move-bpa" ? styles.tabActive : styles.tab}
              onClick={() => setSelectedTab("move-bpa")}
            >
              Move BPAs
            </button>
          )}
          <button
            type="button"
            className={activeTab === "france-to-paris" ? styles.tabActive : styles.tab}
            onClick={() => setSelectedTab("france-to-paris")}
          >
            France → Paris
          </button>
          <button
            type="button"
            className={activeTab === "sync-runs" ? styles.tabActive : styles.tab}
            onClick={() => setSelectedTab("sync-runs")}
          >
            Sync Runs
          </button>
        </div>

        {activeTab === "testimonials" ? (
          <TestimonialsTab />
        ) : activeTab === "processing-items" ? (
          <ProcessingItemsTab />
        ) : activeTab === "raw-testimonials" ? (
          <RawTestimonialsTab />
        ) : activeTab === "fixes" ? (
          <FixesTab />
        ) : activeTab === "france-to-paris" ? (
          <FranceToParisMappedTab />
        ) : activeTab === "sync-runs" ? (
          <SyncRunsTab />
        ) : null}

        {canSeeBatchUpdates && (
          <div hidden={activeTab !== "batch-updates"}>
            <BatchUpdatesTab />
          </div>
        )}

        {canSeeBatchUpdates && (
          <div hidden={activeTab !== "move-bpa"}>
            <MoveBpaTab />
          </div>
        )}
      </div>
    </div>
  );
}

export default PipelineBrowserPage;
